import type { DateTimeFormat, NumberFormat } from '@intlify/core-base'

/**
 * Date, time and number formats, per locale.
 *
 * ## The 24-hour clock is not a preference here
 *
 * Every instant in this product is an incident timestamp, and `2:03` without a meridiem is
 * ambiguous in exactly the conversation where it matters most. French uses a 24-hour clock
 * natively; the English catalogue is pinned to one too (`hour12: false`) rather than inheriting
 * `en-GB`'s defaults, because an operator switching locale to read a support thread must not have
 * the timestamps change shape underneath them.
 *
 * ## Why the zone is named on the long form
 *
 * Everything on the wire is UTC. Everything on screen is the viewer's own zone, with the zone
 * spelled out on the long form — a dashboard that shows a bare `14:03` is unusable the first time
 * two people in different offices read the same incident. The short form drops the zone because it
 * is used in dense contexts (axis ticks, table cells) where it is always paired with the long form
 * in a `title`.
 */

const SHORT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

/** The house long form: `d MMM yyyy HH:mm`, plus seconds and the zone for an audit trail. */
const LONG: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZoneName: 'short',
}

/** `d MMM yyyy HH:mm` exactly — no seconds, no zone. The form used in prose and headings. */
const MEDIUM: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

const DATE: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
}

const TIME: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

/** Axis ticks: the narrowest thing that is still unambiguous within a day's window. */
const TICK: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

const DATE_TIME_SET = {
  short: SHORT,
  medium: MEDIUM,
  long: LONG,
  date: DATE,
  time: TIME,
  tick: TICK,
}

export const datetimeFormats: Record<string, DateTimeFormat> = {
  'fr-FR': DATE_TIME_SET,
  'en-GB': DATE_TIME_SET,
}

/**
 * Number formats.
 *
 * `decimal` is the plain grouped integer — in French that is a narrow no-break space as the group
 * separator and a comma as the decimal mark, which `Intl` gets right and a hand-rolled
 * `toLocaleString` call scattered through components does not.
 *
 * `percent` here formats a number that is **already** a percentage (`99.4` → `99,4 %`), not a
 * ratio. The distinction is deliberate: the uptime resource returns a ratio and every other
 * percentage in the API is already scaled, so one shared formatter that silently multiplied by a
 * hundred would be wrong half the time.
 */
const NUMBER_SET = {
  decimal: { style: 'decimal', maximumFractionDigits: 0 },
  precise: { style: 'decimal', maximumFractionDigits: 2 },
  /** A bare number with one decimal, rendered beside a literal `%` by the caller. */
  percent: { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 },
} as const

export const numberFormats: Record<string, NumberFormat> = {
  'fr-FR': { ...NUMBER_SET },
  'en-GB': { ...NUMBER_SET },
}
